import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import 'chart.js/auto';

interface UserData {
  user_id: number;
  user_name: string;
  zones: string;
  budget_amount: number;
  actual_sales: number;
  cumplimiento_pct: number;
  proyeccion_pesos: number;
  proyeccion_pct: number;
  faltante_pesos: number;
  faltante_pct: number;
}

interface BudgetsByUserProps {
  users: UserData[];
}

export default function BudgetsByUser({ users = [] }: BudgetsByUserProps) {

  const getCumplimientoColor = (pct: number) => {
    if (pct >= 100) return '#2e7d32';
    if (pct >= 70) return '#ed6c02';
    return '#d32f2f';
  };

  /*=====================================================
  CHART
  =====================================================*/
  const barData = {
    labels: users.map(u => u.user_name),
    datasets: [
      {
        label: 'Presupuesto ($)',
        data: users.map(u => u.budget_amount),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: 'Ventas ($)',
        data: users.map(u => u.actual_sales),
        backgroundColor: 'rgba(46, 125, 50, 0.6)',
        borderColor: 'rgba(46, 125, 50, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return `${context.dataset.label}: $${Number(value).toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value: any) => '$' + Number(value).toLocaleString(),
        },
      },
    },
  };

  return (
    <div>
      {/* Chart */}
      <div className="bar-chart" style={{ marginBottom: 24 }}>
        <Bar data={barData} options={chartOptions} />
      </div>

      {/* Table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Comercial</strong></TableCell>
            <TableCell><strong>Zonas</strong></TableCell>
            <TableCell align="right"><strong>Presupuesto ($)</strong></TableCell>
            <TableCell align="right"><strong>Ventas ($)</strong></TableCell>
            <TableCell align="right"><strong>Cumpl. (%)</strong></TableCell>
            <TableCell align="right"><strong>Proy. ($)</strong></TableCell>
            <TableCell align="right"><strong>Proy. (%)</strong></TableCell>
            <TableCell align="right"><strong>Faltante ($)</strong></TableCell>
            <TableCell align="right"><strong>Faltante (%)</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.user_id}>
              <TableCell>{user.user_name}</TableCell>
              <TableCell style={{ fontSize: 12, color: '#666' }}>{user.zones || '-'}</TableCell>
              <TableCell align="right">${Number(user.budget_amount).toLocaleString()}</TableCell>
              <TableCell align="right">${Number(user.actual_sales).toLocaleString()}</TableCell>
              <TableCell align="right">
                <span style={{ 
                  color: getCumplimientoColor(user.cumplimiento_pct),
                  fontWeight: 600 
                }}>
                  {user.cumplimiento_pct}%
                </span>
              </TableCell>
              <TableCell align="right">${Number(user.proyeccion_pesos).toLocaleString()}</TableCell>
              <TableCell align="right">
                <span style={{ color: getCumplimientoColor(user.proyeccion_pct) }}>
                  {user.proyeccion_pct}%
                </span>
              </TableCell>
              <TableCell align="right" style={{ color: '#d32f2f' }}>
                ${Number(user.faltante_pesos).toLocaleString()}
              </TableCell>
              <TableCell align="right" style={{ color: '#d32f2f' }}>
                {user.faltante_pct}%
              </TableCell>
            </TableRow>
          ))}
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center" style={{ padding: 24, color: '#999' }}>
                No hay datos de presupuesto para el período seleccionado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
